import { useState, useEffect } from 'react';
import Card from "./UI/Card";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Legend
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { faList } from "@fortawesome/free-solid-svg-icons";

import classes from "./QuotationsCard.module.css"



const formatPrice = (value) =>
    parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 7,
        maximumFractionDigits: 7,
    });

const COLORS = {
    brl: "#009900",
    usd: "#1e88e5",
    eur: "#fcbe03",
    gbp: "#777",
    jpy: "#BC002D"
};

function formatarDataPTAX(date) {
    const mes = String(date.getMonth() + 1).padStart(2, "0");
    const dia = String(date.getDate()).padStart(2, "0");
    const ano = date.getFullYear();

    return `${mes}-${dia}-${ano}`;
}

/**
 * Faz o merge de múltiplos arrays de moedas.
 * @param {Object} data - Objeto contendo arrays de moedas, ex: { USD: [...], EUR: [...] }
 * @returns {Array} Array de objetos mesclados por data/hora
 */
const mergeCurrencies = (data) => {
    const mergedMap = new Map();

    // Itera sobre cada moeda (ex: 'USD', 'EUR', 'GBP')
    Object.keys(data).forEach((currencyCode) => {
        const currencyArray = data[currencyCode];

        currencyArray.forEach((item) => {
            const timestamp = item.created;

            // Busca o registro existente ou cria um novo com a data
            const existingRecord = mergedMap.get(timestamp) || { created: timestamp };

            // Adiciona a nova moeda ao registro (preservando o que já existe)
            mergedMap.set(timestamp, {
                ...existingRecord,
                ...item
            });
        });
    });

    // Retorna os valores convertidos em array, opcionalmente ordenados por data
    return Array.from(mergedMap.values()).sort((a, b) =>
        new Date(a.created) - new Date(b.created)
    );
};

function normalizarBase100(dados, chave) {
    const primeiroValor = dados.find(d => d[chave] != null)?.[chave];

    if (!primeiroValor) return dados;

    return dados.map(item => ({
        ...item,
        [`${chave}_norm`]: item[chave] != null
            ? (item[chave] / primeiroValor) * 100
            : null
    }));
}

const QuotationsCard = props => {
    const [dados, setDados] = useState([]);
    const [quotations, setQuotations] = useState([]);

    useEffect(() => {
        function normalizarDataISO(dataStr) {
            if (!dataStr) return null;

            // Se já tem Z ou offset (+/-), assume que já está OK
            if (/[zZ]|[+-]\d{2}:\d{2}$/.test(dataStr)) {
                return dataStr.replace(' ', 'T');
            }

            // Caso venha no formato "YYYY-MM-DD HH:mm:ss.SSS"
            // assume horário local e converte para ISO UTC
            const isoLike = dataStr.replace(' ', 'T');

            const date = new Date(isoLike);

            return isNaN(date) ? null : date.toISOString();
        }


        async function carregarHistoricoMoeda(moeda = 'USD', dataInicial, dataFinal, top = 100) {

            const dIni = dataInicial || formatarDataPTAX(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
            const dFin = dataFinal || formatarDataPTAX(new Date());

            // 🔥 Se for BRL, consulta USD e depois inverte
            const moedaConsulta = moeda === 'BRL' ? 'USD' : moeda;

            const urlBcb = `https://olinda.bcb.gov.br/olinda/servico/PTAX/versao/v1/odata/CotacaoMoedaPeriodo(moeda=@moeda,dataInicial=@dataInicial,dataFinalCotacao=@dataFinalCotacao)?@moeda='${moedaConsulta}'&@dataInicial='${dIni}'&@dataFinalCotacao='${dFin}'&$top=${top}&$format=json&$select=cotacaoCompra,dataHoraCotacao`;

            const urlFallback = `https://investidorlibertario.terradata.com.br/api/quotations/currency-history.php?currency=${moeda}`;
            const urlCotacaoAtual = `https://investidorlibertario.terradata.com.br/api/quotations/currency.php`;

            try {
                const [resBcb, resFallback, resCotacao] = await Promise.all([
                    fetch(urlBcb).then(r => r.json()).catch(() => ({ value: [] })),
                    fetch(urlFallback).then(r => r.json()).catch(() => []),
                    fetch(urlCotacaoAtual).then(r => r.json()).catch(() => [])
                ]);

                setQuotations(resCotacao);

                // ✅ Prioriza dados do BCB
                if (resBcb && resBcb.value.length >= 3) {
                    return resBcb.value.map(item => {
                        const dataISO = normalizarDataISO(item.dataHoraCotacao);

                        let valor = item.cotacaoCompra;

                        // 🔥 Inversão para BRL→USD
                        if (moeda === 'BRL') {
                            valor = valor ? (1 / valor) : null;
                        }

                        return {
                            [moeda.toLowerCase()]: valor,
                            created: dataISO ? new Date(dataISO) : null
                        };
                    }).filter(item => item.created !== null && item[moeda.toLowerCase()] !== null);
                }

                // ✅ Fallback normalizado
                return resFallback.map(item => {
                    const dataISO = normalizarDataISO(item.created);

                    let valor = parseFloat(item.value);

                    // 🔥 Inversão também no fallback
                    if (moeda === 'BRL') {
                        valor = valor ? (1 / valor) : null;
                    }

                    return {
                        [moeda.toLowerCase()]: valor,
                        created: dataISO ? new Date(dataISO) : null
                    };
                }).filter(item => item.created !== null && item[moeda.toLowerCase()] !== null);

            } catch (err) {
                console.error(`Erro ao processar moeda ${moeda}:`, err);
                return [];
            }
        }

        async function carregarDados() {
            // Carrega todas as moedas em paralelo para melhor performance
            const moedas = ['BRL', 'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'ARS'];

            const resultados = await Promise.all(
                moedas.map(m => carregarHistoricoMoeda(m))
            );

            const currenciesData = {};
            moedas.forEach((m, index) => {
                currenciesData[m] = normalizarBase100(resultados[index], m.toLowerCase());
            });

            setDados(mergeCurrencies(currenciesData));
        }

        carregarDados();
    }, []);

    return (
        <Card>
            <h4 className={classes.title}><FontAwesomeIcon icon={faStar} />&nbsp;{props.title}</h4>
            <ul>
                {quotations.map(quotation =>
                    <li key={quotation.id} style={{ borderBottom: '1px dotted #ccc', padding: 1 }}>
                        <span style={{ color: COLORS[quotation.ticker.toLowerCase()] }}><strong>{quotation.ticker}</strong></span>
                        <span className={classes.right} style={{ color: quotation.color }}>R$ {formatPrice(quotation.value)}</span>
                    </li>
                )}
            </ul>

            <ResponsiveContainer width="100%" height={300}>
                <LineChart
                    data={dados}
                    margin={{ top: 10, right: 15, left: 5, bottom: 5 }}
                >
                    <CartesianGrid
                        stroke="#f0f0f0"
                        vertical={false}
                    />

                    <XAxis
                        dataKey="created"
                        tick={{ fill: "#666", fontSize: 12 }}
                        axisLine={{ stroke: "#e0e0e0" }}
                        tickLine={false}
                        tickFormatter={(value) => {
                            const data = new Date(value);
                            return data.toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                            });
                        }}
                    />

                    <YAxis
                        width={60}
                        domain={['auto', 'auto']}
                        tickFormatter={(value) =>
                            `${(value - 100).toFixed(2)}%`
                        }
                        tick={{ fill: "#666", fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                    />

                    <Tooltip
                        labelFormatter={(label) =>
                            new Date(label).toLocaleString('pt-BR')
                        }
                        formatter={(value) =>
                            `${(value - 100).toFixed(2)}%`
                        }
                        contentStyle={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #e0e0e0",
                            borderRadius: "6px",
                            fontSize: "12px"
                        }}
                    />
                    <Legend verticalAlign="top" height={36} />

                    <Line
                        type="monotone"
                        dataKey="brl_norm"
                        name="BRL"
                        stroke="#009900"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls
                    />

                    <Line
                        type="monotone"
                        dataKey="usd_norm"
                        name="USD"
                        stroke="#1e88e5"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls
                    />

                    <Line
                        type="monotone"
                        dataKey="eur_norm"
                        name="EUR"
                        stroke="#fcbe03"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls
                    />

                    <Line
                        type="monotone"
                        dataKey="gbp_norm"
                        name="GBP"
                        stroke="#777"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls
                    />

                    <Line
                        type="monotone"
                        dataKey="jpy_norm"
                        name="JPY"
                        stroke="#BC002D"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls
                    />
                </LineChart>
            </ResponsiveContainer>

            <a href="/quotations" className="btn btn-primary btn-sm w-100">
                <FontAwesomeIcon icon={faList} /> Ver listagem completa
            </a>
        </Card>
    )
};

export default QuotationsCard;